import { useEffect, useState } from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileText,
  LinkIcon,
  Loader2,
  Receipt,
  Unlink,
} from "lucide-react";

import { AssignPaymentDialog } from "~/components/AssignPaymentDialog";
import { UnlinkPaymentDialog } from "~/components/UnlinkPaymentDialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { formatCurrency } from "~/utils/helpers";

interface BankTransaction {
  id: string;
  amount: number;
  description: string;
  transaction_date: string;
  sender_account_name: string;
  sender_account_holder_id: string;
  is_linked: boolean;
  linked_to_type?: "sales_request" | "manual_factura";
  linked_client_name?: string;
  linked_document_info?: string;
  sales_request_id?: string;
  manual_factura_id?: string;
  total_assigned?: number;
  remaining_balance?: number;
}

export function BankTransactionsList() {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BankTransaction | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const { toast } = useToast();

  const fetchTransactions = async () => {
    try {
      setLoading(true);

      // Fetch a page of positive movements (newest first). Both this query and
      // the payment lookup below stay well under the PostgREST 10k-row response
      // cap that was silently dropping the newest assignments (which made
      // freshly-assigned movements render as "Sin asignar").
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const {
        data: allMovements,
        error: movementsError,
        count,
      } = await supabase
        .from("rail_movements")
        .select("*", { count: "exact" })
        .gt("amount", 0) // Only positive amounts (incoming payments)
        // Rail cutover: only movements after Fintoc's last day (2026-08-06).
        .gt("post_date", "2026-08-06")
        .order("transaction_date", { ascending: false })
        .range(from, to);

      if (movementsError) throw movementsError;

      setTotalCount(count || 0);

      // Payment/link info only for the movements shown on this page. Scoping by
      // id keeps the response tiny and immune to the row cap.
      const pageMovementIds = (allMovements || []).map(
        (movement) => movement.id
      );
      const { data: paymentInfo, error: paymentInfoError } = await supabase.rpc(
        "get_transactions_payment_info",
        {
          p_movement_ids: pageMovementIds,
        }
      );

      if (paymentInfoError) throw paymentInfoError;

      // Sum assigned amounts per movement (a movement can back several payments).
      const movementTotals = new Map<string, number>();
      paymentInfo?.forEach((payment) => {
        if (payment.rail_movement_id) {
          const current = movementTotals.get(payment.rail_movement_id) || 0;
          movementTotals.set(
            payment.rail_movement_id,
            current + (payment.payment_amount || 0)
          );
        }
      });

      // Keep the first payment's link info per movement.
      const linkedMovementInfo = new Map<
        string,
        {
          type: "sales_request" | "manual_factura";
          clientName: string;
          documentInfo: string;
          salesRequestId?: string;
          manualFacturaId?: string;
        }
      >();
      paymentInfo?.forEach((payment) => {
        if (
          payment.rail_movement_id &&
          !linkedMovementInfo.has(payment.rail_movement_id)
        ) {
          linkedMovementInfo.set(payment.rail_movement_id, {
            type: payment.payment_type as "sales_request" | "manual_factura",
            clientName: payment.client_name || "Cliente desconocido",
            documentInfo: payment.document_info || "Documento desconocido",
            salesRequestId: payment.sales_request_id || undefined,
            manualFacturaId: payment.manual_factura_id || undefined,
          });
        }
      });

      // Process the transactions data and mark linked transactions
      const processedData =
        allMovements?.map((transaction) => {
          const linkedInfo = linkedMovementInfo.get(transaction.id);
          const totalAssigned = movementTotals.get(transaction.id) || 0;
          const remainingBalance = Math.max(
            0,
            transaction.amount - totalAssigned
          );

          return {
            ...transaction,
            sender_account_name: transaction.sender_account_name || "",
            sender_account_holder_id:
              transaction.sender_account_holder_id || "",
            is_linked: !!linkedInfo,
            linked_to_type: linkedInfo?.type,
            linked_sales_request_id: linkedInfo?.salesRequestId || null,
            linked_manual_factura_id: linkedInfo?.manualFacturaId || null,
            linked_client_name: linkedInfo?.clientName,
            linked_document_info: linkedInfo?.documentInfo,
            total_assigned: totalAssigned,
            remaining_balance: remainingBalance,
          };
        }) || [];

      setTransactions(processedData);
    } catch (err) {
      console.error("Error fetching bank transactions:", err);
      setError(
        err instanceof Error ? err.message : "Error loading bank transactions"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPayment = (transaction: BankTransaction) => {
    setSelectedTransaction(transaction);
    setIsDialogOpen(true);
  };

  const handleUnlinkPayment = (transaction: BankTransaction) => {
    setSelectedTransaction(transaction);
    setIsUnlinkDialogOpen(true);
  };

  const handlePaymentsUnlinked = (unlinkedAmount: number) => {
    // Update the specific transaction that had payments unlinked
    if (selectedTransaction) {
      setTransactions((prevTransactions) =>
        prevTransactions.map((transaction) =>
          transaction.id === selectedTransaction.id
            ? {
                ...transaction,
                total_assigned: Math.max(
                  0,
                  (transaction.total_assigned || 0) - unlinkedAmount
                ),
                remaining_balance: Math.min(
                  transaction.amount,
                  (transaction.remaining_balance || 0) + unlinkedAmount
                ),
                is_linked:
                  (transaction.total_assigned || 0) - unlinkedAmount > 0, // Still linked if there are remaining assignments
              }
            : transaction
        )
      );
    }

    setSelectedTransaction(null);
    setIsUnlinkDialogOpen(false);
  };

  const handlePaymentAssigned = (
    documentId?: string,
    assignedAmount?: number
  ) => {
    setIsDialogOpen(false);

    // Update only the specific transaction that was just linked
    if (selectedTransaction && assignedAmount) {
      setTransactions((prevTransactions) =>
        prevTransactions.map((transaction) =>
          transaction.id === selectedTransaction.id
            ? {
                ...transaction,
                is_linked: true,
                linked_to_type: undefined, // Will be determined by next fetch
                linked_client_name: undefined,
                linked_document_info: undefined,
                total_assigned:
                  (transaction.total_assigned || 0) + assignedAmount,
                remaining_balance: Math.max(
                  0,
                  transaction.amount -
                    ((transaction.total_assigned || 0) + assignedAmount)
                ),
              }
            : transaction
        )
      );
    }

    setSelectedTransaction(null);
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (error) {
    return <div className="text-red-600">Error: {error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Transacciones Bancarias</h2>
        <Button variant="outline" onClick={fetchTransactions}>
          Actualizar
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Cargando transacciones...</span>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Monto</TableHead>
                <TableHead>Remitente</TableHead>
                <TableHead>RUT</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Factura Manual</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-6 text-center">
                    No hay transacciones para mostrar
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction, index) => (
                  <TableRow
                    key={transaction.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      {transaction.transaction_date
                        ? format(
                            new Date(transaction.transaction_date),
                            "dd MMM yyyy HH:mm",
                            { locale: es }
                          )
                        : "N/A"}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(transaction.amount)}
                    </TableCell>
                    <TableCell>
                      {transaction.sender_account_name || "Desconocido"}
                    </TableCell>
                    <TableCell>
                      {transaction.sender_account_holder_id || "N/A"}
                    </TableCell>
                    <TableCell className="max-w-md truncate">
                      {transaction.description}
                    </TableCell>
                    <TableCell>
                      {transaction.is_linked ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            {transaction.linked_to_type === "sales_request" ? (
                              <>
                                <FileText className="h-4 w-4 text-blue-600" />
                                <Badge
                                  variant="success"
                                  className="bg-blue-100 text-blue-800"
                                >
                                  Nota de Venta
                                </Badge>
                              </>
                            ) : transaction.linked_to_type ===
                              "manual_factura" ? (
                              <>
                                <Receipt className="h-4 w-4 text-green-600" />
                                <Badge
                                  variant="success"
                                  className="bg-green-100 text-green-800"
                                >
                                  Factura Manual
                                </Badge>
                              </>
                            ) : (
                              <Badge
                                variant="success"
                                className="bg-green-100 text-green-800"
                              >
                                Asignado
                              </Badge>
                            )}
                          </div>
                          {transaction.total_assigned !== undefined &&
                            transaction.remaining_balance !== undefined &&
                            transaction.remaining_balance > 0 && (
                              <div className="text-xs text-orange-600">
                                Asignado:{" "}
                                {formatCurrency(transaction.total_assigned)}
                                <br />
                                Disponible:{" "}
                                {formatCurrency(transaction.remaining_balance)}
                              </div>
                            )}
                        </div>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-yellow-100 text-yellow-800"
                        >
                          Sin asignar
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.manual_factura_id ? (
                        <div className="flex flex-col">
                          <div className="text-sm font-medium">
                            {transaction.manual_factura_id.substring(0, 8)}...
                          </div>
                          <div className="text-xs text-gray-500">
                            ID de Factura
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {transaction.amount > 0 && (
                        <div className="flex items-center justify-end gap-1">
                          {transaction.is_linked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnlinkPayment(transaction)}
                              title="Desvincular pago"
                            >
                              <Unlink className="h-4 w-4" />
                            </Button>
                          )}
                          {(!transaction.is_linked ||
                            (transaction.remaining_balance &&
                              transaction.remaining_balance > 0)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAssignPayment(transaction)}
                              title={
                                transaction.is_linked
                                  ? "Asignar saldo restante"
                                  : "Asignar pago"
                              }
                            >
                              <LinkIcon className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && totalCount > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {totalCount.toLocaleString("es-CL")} transacciones
          </span>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 text-sm">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {selectedTransaction && (
        <AssignPaymentDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          transaction={selectedTransaction}
          onPaymentAssigned={handlePaymentAssigned}
        />
      )}

      {selectedTransaction && (
        <UnlinkPaymentDialog
          open={isUnlinkDialogOpen}
          onOpenChange={setIsUnlinkDialogOpen}
          transaction={selectedTransaction}
          onPaymentsUnlinked={handlePaymentsUnlinked}
        />
      )}
    </div>
  );
}
